import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseQueryResult } from 'react-query';

import type {
  GetAllOpenQuestionAnswersResponse,
  GetSurveyResultResponse,
  GetUserQuestionAnswersRankingResponse,
} from '@genially/contracts';

import type { SelectedInteractiveQuestion } from '../SidebarSummary.context';
import { SidebarType } from '../types/SidebarType';
import { calculateSidebarTypeFromSelectedQuestion } from '../utils/calculateSidebarTypeFromSelectedQuestion';

export const useFetchAggregatedResults = <
  T extends
    | GetAllOpenQuestionAnswersResponse
    | GetSurveyResultResponse
    | GetUserQuestionAnswersRankingResponse,
>({
  shouldFetch,
  query,
  isSidebarOpen,
  selectedQuestion,

  options,
}: {
  query: UseQueryResult<T>;
  shouldFetch: boolean;
  selectedQuestion?: SelectedInteractiveQuestion;
  isSidebarOpen: boolean;

  options: {
    fetchRefreshTime: number;
    maxCalls: number;
  };
}) => {
  const { fetchRefreshTime, maxCalls } = options;

  const isRankingType =
    calculateSidebarTypeFromSelectedQuestion(selectedQuestion) === SidebarType.Ranking;

  const [lastUpdated, setLastUpdated] = useState<Date | undefined>(undefined);

  const interval = useRef<NodeJS.Timer | undefined>(undefined);

  const callCount = useRef(0);

  const { refetch } = query;

  const fetchData = useCallback(async () => {
    const response = await refetch();

    if (response) {
      setLastUpdated(new Date());
    }
  }, [refetch]);

  const stopTimer = () => {
    if (!interval.current) {
      return;
    }

    clearInterval(interval.current);
    interval.current = undefined;
  };

  useEffect(() => {
    callCount.current = 0;
    stopTimer();
  }, [selectedQuestion]);

  useEffect(() => {
    if (!shouldFetch || !isSidebarOpen) return undefined;

    if (!selectedQuestion && !isRankingType) return undefined;

    if (interval.current !== undefined) return undefined;

    const fetchInterval = () => {
      callCount.current += 1;

      if (callCount.current >= maxCalls) {
        stopTimer();
      }

      fetchData();
    };

    fetchInterval();

    interval.current = setInterval(fetchInterval, fetchRefreshTime);

    return () => {
      stopTimer();
    };
  }, [
    fetchData,
    fetchRefreshTime,
    isRankingType,
    isSidebarOpen,
    maxCalls,
    refetch,
    selectedQuestion,
    shouldFetch,
  ]);

  useEffect(() => {
    if (!shouldFetch || !isSidebarOpen) {
      stopTimer();
    }
  }, [isSidebarOpen, shouldFetch]);

  return { data: query.data, lastUpdated, fetchAggregatedData: fetchData };
};
